import dayjs from "dayjs";
import { InfoIcon } from "lucide-react";
import { AlertDialog } from "radix-ui";
import { useRef, useState } from "react";
import type { Control, FieldPath } from "react-hook-form";
import { useController } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import { Checkbox } from "../../../../../components/ui/checkbox";
import { Input } from "../../../../../components/ui/input";
import { Label } from "../../../../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../components/ui/select";
import { cn } from "../../../../../lib/utils";
import { INCOME_ORDINAL_LABELS, PAY_FREQUENCY_OPTIONS } from "../constants";
import { calculateIncomeBreakdown } from "../incomeCalculator/incomeCalculator";
import type { PayFrequency } from "../incomeCalculator/incomeCalculator.constants";
import type { IncomeBreakdownResult } from "../incomeCalculator/incomeCalculator.types";
import { useFormReadOnly } from "../readOnlyContext";
import type { IncomeInput, SignersInput } from "../Signers.schema";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type PersonPath = "driver" | "secondSigner";

interface IncomeCalculatorPanelProperties {
  control: Control<SignersInput>;
  personPath: PersonPath;
  index: number;
  panelId: string;
  /** Called after a successful Calculate with the full breakdown. */
  onCalculated: (result: IncomeBreakdownResult) => void;
  /** Called when the Gross Pay Per Period input loses focus (recomputes Gross Monthly Pay). */
  onPayPerPeriodBlur?: () => void;
}

// ---------------------------------------------------------------------------
// USD formatter — reused by the results table
// ---------------------------------------------------------------------------
const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function formatUsd(value: number): string {
  return USD_FORMATTER.format(value);
}

// ---------------------------------------------------------------------------
// Digit count helper for the ≤12 digits rule (§7 P1).
// Strips commas, spaces, and the decimal point before counting.
// ---------------------------------------------------------------------------
function digitCount(raw: string): number {
  return raw.replaceAll(/\D/g, "").length;
}

// ---------------------------------------------------------------------------
// Money-field validation helper — §7 P1 numeric-only / min / max / digit rules.
// Returns the i18n key suffix for the failed rule (caller supplies the label via
// t() interpolation), or undefined when the value is valid.
// Returning a key rather than a message keeps this helper free of React hooks
// and avoids TFunction overload conflicts when passing `t` as a callback.
// ---------------------------------------------------------------------------
type MoneyValidationKey = "numericOnly" | "minValue" | "maxValue" | "maxDigits";

function validateMoneyField(raw: string): MoneyValidationKey | undefined {
  if (!raw) {
    return undefined; // required check is done separately
  }
  const numeric = Number(raw);
  if (Number.isNaN(numeric)) {
    return "numericOnly";
  }
  if (numeric < 1) {
    return "minValue";
  }
  if (numeric > 999_999) {
    return "maxValue";
  }
  if (digitCount(raw) > 12) {
    return "maxDigits";
  }
  return undefined;
}

// ---------------------------------------------------------------------------
// P2 — Stip% color classes: green when 90 ≤ stipPct ≤ 110, red otherwise.
// Moved to module scope to satisfy unicorn/consistent-function-scoping.
// ---------------------------------------------------------------------------
function stipColorClass(stipPct: number): string {
  if (stipPct >= 90 && stipPct <= 110) {
    return "border-success-strong bg-success-subtle text-success-strong";
  }
  return "border-destructive/40 bg-destructive/10 text-destructive";
}

// ---------------------------------------------------------------------------
// IncomeCalculatorPanel
// Inline collapsible panel. Calculator-local state holds the five inputs.
// The hire date is read from RHF to pass to the pay-period algorithm.
// ---------------------------------------------------------------------------
export function IncomeCalculatorPanel({
  control,
  personPath,
  index,
  panelId,
  onCalculated,
  onPayPerPeriodBlur,
}: IncomeCalculatorPanelProperties) {
  const { t } = useTranslation();
  // Radix Select ignores the enclosing disabled <fieldset>, so honour the lock.
  const readOnly = useFormReadOnly();

  // Ordinal label for income-level validation messages (§7 P1).
  const ordinal = INCOME_ORDINAL_LABELS[index] ?? `${index + 1}th`;

  // Helper to build a typed field path for the income at this index.
  function incomePath(field: keyof IncomeInput): FieldPath<SignersInput> {
    return `${personPath}.incomes.${index}.${field}` as FieldPath<SignersInput>;
  }

  // Helper to build the person-level path (dateOfBirth lives on the person, not the income).
  function personInfoPath(field: "dateOfBirth"): FieldPath<SignersInput> {
    return `${personPath}.customerInfo.${field}` as FieldPath<SignersInput>;
  }

  // ---------------------------------------------------------------------------
  // Calculator inputs wired to RHF so they survive collapse/remount (FIX #1).
  // payFrequency and payPerPeriod already exist as schema fields from OR-21764.
  // payDate, payPeriodEndDate, grossPayYtd are additive optional fields added
  // in OR-21766. grossNet (Gross/Net pay type) is read here so the Net-pay
  // ×1.21 gross-up is applied to both income and tax bases (OR-22309 Phase 3a).
  // isPaidOn1stAnd15 is the OR-22309 P3 toggle (2x-Monthly only).
  // Results and validation errors remain block-local ephemeral state.
  // ---------------------------------------------------------------------------
  const { field: payFrequencyField } = useController({ control, name: incomePath("payFrequency") });
  const { field: payPerPeriodField } = useController({ control, name: incomePath("payPerPeriod") });
  const { field: grossPayYtdField } = useController({ control, name: incomePath("grossPayYtd") });
  const { field: payDateField } = useController({ control, name: incomePath("payDate") });
  const { field: payPeriodEndDateField } = useController({ control, name: incomePath("payPeriodEndDate") });
  const { field: hireDateField } = useController({ control, name: incomePath("hireDate") });
  const { field: grossNetField } = useController({ control, name: incomePath("grossNet") });
  const { field: isPaidOn1stAnd15Field } = useController({ control, name: incomePath("isPaidOn1stAnd15") });

  // DOB lives on the person (customerInfo), not on the income.
  const { field: dateOfBirthField } = useController({ control, name: personInfoPath("dateOfBirth") });

  // Normalize RHF values to the string/typed-string shapes the panel needs.
  const localPayFrequency = payFrequencyField.value == null ? "" : String(payFrequencyField.value);
  // grossPayPerPeriod is stored as a number in payPerPeriod; display as a string.
  const localGrossPayPerPeriod =
    payPerPeriodField.value == null || payPerPeriodField.value === 0 ? "" : String(payPerPeriodField.value);
  const localGrossPayYtd = grossPayYtdField.value == null ? "" : String(grossPayYtdField.value);
  const localPayDate = payDateField.value == null ? "" : String(payDateField.value);
  const localPayPeriodEndDate = payPeriodEndDateField.value == null ? "" : String(payPeriodEndDateField.value);
  const hireDate = hireDateField.value == null ? "" : String(hireDateField.value);
  // grossNet drives the Net-pay ×1.21 gross-up (OR-22309 Phase 3a).
  const payType: "Gross" | "Net" = grossNetField.value === "Net" ? "Net" : "Gross";
  // isPaidOn1stAnd15 — boolean toggle (OR-22309 P3).
  const isPaidOn1stAnd15: boolean = isPaidOn1stAnd15Field.value === true;
  // DOB string (ISO YYYY-MM-DD or empty) — used for date validations (§7 P1).
  const dateOfBirth: string = dateOfBirthField.value == null ? "" : String(dateOfBirthField.value);

  // Results + validation errors — ephemeral (reset when panel is remounted)
  const [result, setResult] = useState<IncomeBreakdownResult | null>(null);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});

  // Ratio alert dialog — opens after Calculate when ratioThreshold !== "ok".
  // Re-fires on every Calculate that yields an out-of-band ratio.
  const [ratioAlertOpen, setRatioAlertOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // P4 — Data-loss confirm dialog state.
  // pendingEdit holds the value to apply after the user confirms.
  // ---------------------------------------------------------------------------
  type PendingEdit = { field: "grossPayPerPeriod"; value: number | undefined };

  const [confirmOpen, setConfirmOpen] = useState(false);
  const pendingEditReference = useRef<PendingEdit | null>(null);

  /** True when pay-stub data exists that would be cleared by editing grossPayPerPeriod. */
  function hasPayStubData(): boolean {
    return Boolean(localGrossPayYtd || localPayDate || localPayPeriodEndDate || result !== null);
  }

  /** Clears grossPayYtd, payDate, payPeriodEndDate, and the computed result. */
  function clearPayStubData() {
    grossPayYtdField.onChange();
    payDateField.onChange();
    payPeriodEndDateField.onChange();
    setResult(null);
    setErrors({});
  }

  // ---------------------------------------------------------------------------
  // Validation — calculator-scoped; does not touch RHF block validity.
  // FIX #2: when hire year === pay year, payPeriodEndDate is required because
  // calculatePayPeriods uses it as the upper bound (matching dealer-ui superRefine).
  // P1 (OR-22309): adds §7 money-field and DOB-based date validations.
  // ---------------------------------------------------------------------------
  function validate(): boolean {
    const next: Partial<Record<string, string>> = {};
    const required = t("review.income.calculator.errors.required");

    // Field labels for money-field error messages.
    const periodLabel = t("review.income.calculator.grossPayPerPeriod");
    const ytdLabel = t("review.income.calculator.grossPayYtd");

    if (hireDate) {
      // §7.4 Hire Date validations: future check + DOB check.
      const today = dayjs().format("YYYY-MM-DD");
      if (hireDate > today) {
        next.hireDate = t("review.income.calculator.errors.hireDateFuture");
      } else if (dateOfBirth && hireDate < dateOfBirth) {
        next.hireDate = t("review.income.calculator.errors.hireDateBeforeDob");
      }
    } else {
      next.hireDate = required;
    }

    if (!localPayFrequency) {
      next.payFrequency = required;
    }

    // Gross Pay Per Period — required + §7 money rules.
    if (localGrossPayPerPeriod) {
      const moneyKey = validateMoneyField(localGrossPayPerPeriod);
      if (moneyKey) {
        next.grossPayPerPeriod = t(`review.income.calculator.errors.${moneyKey}`, { label: periodLabel });
      }
    } else {
      next.grossPayPerPeriod = required;
    }

    if (localGrossPayYtd) {
      const moneyKey = validateMoneyField(localGrossPayYtd);
      if (moneyKey) {
        next.grossPayYtd = t(`review.income.calculator.errors.${moneyKey}`, { label: ytdLabel });
      }
    } else {
      next.grossPayYtd = required;
    }

    // Compare ISO date strings directly: for "YYYY-MM-DD" lexicographic order
    // === chronological order, which is timezone-safe. Using new Date(str)
    // parses as UTC midnight and mis-compares against a local "now" (the same
    // class of bug fixed for the paid-on-15th warning).
    const today = dayjs().format("YYYY-MM-DD");

    if (localPayDate) {
      if (localPayDate > today) {
        next.payDate = t("review.income.calculator.errors.payDateFuture");
      } else if (hireDate && !next.hireDate && localPayDate < hireDate) {
        next.payDate = t("review.income.calculator.errors.payDateBeforeHire");
      } else if (dateOfBirth && localPayDate < dateOfBirth) {
        // §7.3 Pay Date < DOB. Uses {{ord}} (not {{ordinal}}) to avoid i18next
        // reserved option key conflict (`ordinal: boolean` in TOptions).
        next.payDate = t("review.income.calculator.errors.payDateBeforeDob", { ord: ordinal });
      }
    } else {
      next.payDate = required;
    }

    if (localPayPeriodEndDate) {
      if (localPayPeriodEndDate > today) {
        next.payPeriodEndDate = t("review.income.calculator.errors.payPeriodEndDateFuture");
      } else if (hireDate && !next.hireDate && localPayPeriodEndDate < hireDate) {
        next.payPeriodEndDate = t("review.income.calculator.errors.payPeriodEndDateBeforeHire");
      } else if (localPayDate && localPayPeriodEndDate > localPayDate) {
        next.payPeriodEndDate = t("review.income.calculator.errors.payPeriodEndDateAfterPayDate");
      } else if (dateOfBirth && localPayPeriodEndDate < dateOfBirth) {
        // §7.5 Pay Period End Date < DOB. Uses {{ord}} (not {{ordinal}}) to avoid
        // i18next reserved option key conflict.
        next.payPeriodEndDate = t("review.income.calculator.errors.payPeriodEndDateBeforeDob", { ord: ordinal });
      }
    } else {
      // FIX #2 — same-year rule: when hire year matches pay date year, the
      // pay period end date is required as the upper bound for the pay-period
      // loop (mirrors dealer-ui's step2CreditApplicationSchema superRefine).
      const payYear = localPayDate ? localPayDate.slice(0, 4) : null;
      const hireYear = hireDate ? hireDate.slice(0, 4) : null;
      if (payYear && hireYear && payYear === hireYear) {
        next.payPeriodEndDate = t("review.income.calculator.errors.payPeriodEndDateRequiredSameYear");
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleCalculate() {
    if (!validate()) {
      return;
    }

    const breakdown = calculateIncomeBreakdown({
      payFrequency: localPayFrequency as PayFrequency,
      grossPayPerPeriod: Number(localGrossPayPerPeriod),
      grossPayYtd: Number(localGrossPayYtd),
      payDate: localPayDate,
      payPeriodEndDate: localPayPeriodEndDate || undefined,
      hireDate,
      isPaidOn1stAnd15,
      payType,
    });

    setResult(breakdown);
    onCalculated(breakdown);
    setRatioAlertOpen(breakdown.ratioThreshold !== "ok");
  }

  // ---------------------------------------------------------------------------
  // P3 — Paid-on-15 warning variants (§4.7).
  // When 2x-Monthly AND pay-date day === 15:
  //   isPaidOn1stAnd15 false → "Customer is paid on the 1st and 15th."
  //   isPaidOn1stAnd15 true  → "Customer is paid on the 15th and 30th."
  // ---------------------------------------------------------------------------
  const isPayDateOnFifteenth =
    // Compare the day component of the YYYY-MM-DD string directly: parsing via
    // `new Date(localPayDate)` reads as UTC midnight then `.getDate()` shifts it
    // into local time, so west-of-UTC users would never see the 15th-day warning.
    localPayDate.split("-")[2] === "15" && localPayFrequency === "2x-Monthly";

  // Resolve the warning message inline to avoid passing a dynamic key to t() (TS strict typing).
  const paidOn15Warning: string | null = isPayDateOnFifteenth
    ? isPaidOn1stAnd15
      ? t("review.income.calculator.warnings.paid15And30")
      : t("review.income.calculator.warnings.paidOn1And15")
    : null;

  return (
    <div id={panelId} className="bg-surface-subtle border-primary/30 rounded-md border px-3 py-3 shadow-sm">
      {/* Inputs grid — compact: 1 col mobile, 2 col sm/md, 3 col lg, 4 col xl+ */}
      <div className="grid grid-cols-1 gap-x-1 gap-y-0 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {/* Hire Date — first field; shares RHF state with the outer row so the
            value persists when the calculator is toggled. */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-hiredate`} className="text-xs">
            {t("review.fields.hireDate")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id={`${panelId}-hiredate`}
            type="date"
            value={hireDate}
            onChange={(event) => {
              hireDateField.onChange(event.target.value);
            }}
            aria-invalid={errors.hireDate ? true : undefined}
            aria-describedby={errors.hireDate ? `${panelId}-hiredate-err` : undefined}
            className="w-full pr-1"
          />
          {errors.hireDate && (
            <p id={`${panelId}-hiredate-err`} role="alert" className="text-destructive text-2xs">
              {errors.hireDate}
            </p>
          )}
        </div>

        {/* Pay Frequency */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-freq`} className="text-xs">
            {t("review.income.calculator.payFrequency")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Select
            value={localPayFrequency}
            onValueChange={(value) => {
              payFrequencyField.onChange(value as PayFrequency);
            }}
            disabled={readOnly}
          >
            <SelectTrigger
              id={`${panelId}-freq`}
              aria-label={t("review.income.calculator.payFrequency")}
              aria-invalid={errors.payFrequency ? true : undefined}
              aria-describedby={errors.payFrequency ? `${panelId}-freq-err` : undefined}
              className="w-full"
              disabled={readOnly}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PAY_FREQUENCY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.payFrequency && (
            <p id={`${panelId}-freq-err`} role="alert" className="text-destructive text-2xs">
              {errors.payFrequency}
            </p>
          )}
        </div>

        {/* Gross Pay Per Period — P4: confirm before edit when pay-stub data exists */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-period`} className="text-xs">
            {t("review.income.calculator.grossPayPerPeriod")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id={`${panelId}-period`}
            type="number"
            min={0}
            step="0.01"
            value={localGrossPayPerPeriod}
            onChange={(event) => {
              const v = event.target.value;
              const newValue = v === "" ? undefined : Number(v);

              // P4: if pay-stub data exists, show confirm dialog before applying
              // the edit. Otherwise apply directly.
              if (hasPayStubData()) {
                pendingEditReference.current = { field: "grossPayPerPeriod", value: newValue };
                setConfirmOpen(true);
              } else {
                payPerPeriodField.onChange(newValue);
              }
            }}
            onBlur={() => {
              payPerPeriodField.onBlur();
              onPayPerPeriodBlur?.();
            }}
            aria-invalid={errors.grossPayPerPeriod ? true : undefined}
            aria-describedby={errors.grossPayPerPeriod ? `${panelId}-period-err` : undefined}
            className="w-full"
          />
          {errors.grossPayPerPeriod && (
            <p id={`${panelId}-period-err`} role="alert" className="text-destructive text-2xs">
              {errors.grossPayPerPeriod}
            </p>
          )}
        </div>

        {/* Gross Pay YTD */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-ytd`} className="text-xs">
            {t("review.income.calculator.grossPayYtd")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id={`${panelId}-ytd`}
            type="number"
            min={0}
            step="0.01"
            value={localGrossPayYtd}
            onChange={(event) => {
              const v = event.target.value;
              grossPayYtdField.onChange(v === "" ? undefined : Number(v));
            }}
            onBlur={grossPayYtdField.onBlur}
            aria-invalid={errors.grossPayYtd ? true : undefined}
            aria-describedby={errors.grossPayYtd ? `${panelId}-ytd-err` : undefined}
            className="w-full"
          />
          {errors.grossPayYtd && (
            <p id={`${panelId}-ytd-err`} role="alert" className="text-destructive text-2xs">
              {errors.grossPayYtd}
            </p>
          )}
        </div>

        {/* Pay Date */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-paydate`} className="text-xs">
            {t("review.income.calculator.payDate")}
            <span className="text-destructive ml-0.5" aria-hidden="true">
              *
            </span>
          </Label>
          <Input
            id={`${panelId}-paydate`}
            type="date"
            value={localPayDate}
            onChange={(event) => {
              payDateField.onChange(event.target.value);
            }}
            aria-invalid={errors.payDate ? true : undefined}
            aria-describedby={errors.payDate ? `${panelId}-paydate-err` : undefined}
            className="w-full pr-1"
          />
          {errors.payDate && (
            <p id={`${panelId}-paydate-err`} role="alert" className="text-destructive text-2xs">
              {errors.payDate}
            </p>
          )}
        </div>

        {/* Pay Period End Date (optional) */}
        <div className="flex flex-col gap-0.5">
          <Label htmlFor={`${panelId}-enddate`} className="text-xs">
            {t("review.income.calculator.payPeriodEndDate")}
          </Label>
          <Input
            id={`${panelId}-enddate`}
            type="date"
            value={localPayPeriodEndDate}
            onChange={(event) => {
              payPeriodEndDateField.onChange(event.target.value);
            }}
            aria-invalid={errors.payPeriodEndDate ? true : undefined}
            aria-describedby={errors.payPeriodEndDate ? `${panelId}-enddate-err` : undefined}
            className="w-full pr-1"
          />
          {errors.payPeriodEndDate && (
            <p id={`${panelId}-enddate-err`} role="alert" className="text-destructive text-2xs">
              {errors.payPeriodEndDate}
            </p>
          )}
        </div>

        {/* P3 — isPaidOn1stAnd15 checkbox: shown only when payFrequency is 2x-Monthly.
            Label describes the CHECKED state ("Paid on the 15th and 30th") so it matches
            the message shown when checked. Legacy semantics: isPaidOn1stAnd15=true → "15th
            and 30th"; false (default) → "1st and 15th". The schema field name is kept for
            BFF/legacy parity even though the label intentionally reads the other way. */}
        {localPayFrequency === "2x-Monthly" && (
          <div className="flex flex-col gap-0.5">
            <span aria-hidden="true" className="hidden h-4 sm:block" />
            <div className="flex items-center gap-2">
              <Checkbox
                id={`${panelId}-paid1and15`}
                checked={isPaidOn1stAnd15}
                onCheckedChange={(checked) => {
                  isPaidOn1stAnd15Field.onChange(checked === true);
                }}
                aria-label={t("review.income.calculator.isPaidOn1stAnd15")}
              />
              <label htmlFor={`${panelId}-paid1and15`} className="cursor-pointer text-xs font-normal">
                {t("review.income.calculator.isPaidOn1stAnd15")}
              </label>
            </div>
          </div>
        )}

        {/* Calculate button — grid cell next to Pay Period End Date; the
            label-height spacer keeps it level with the inputs. */}
        <div className="flex flex-col gap-0.5">
          <span aria-hidden="true" className="hidden h-4 sm:block" />
          <Button type="button" variant="default" size="xs" onClick={handleCalculate} className="w-full">
            {t("review.income.calculator.calculate")}
          </Button>
        </div>
      </div>

      {/* P3 — Inline warning — paid-on-15 two-variant (§4.7) */}
      {paidOn15Warning !== null && (
        <div className="mt-3 flex flex-col gap-1.5">
          <div
            role="alert"
            className="bg-info/10 border-info/40 text-info-foreground flex items-start gap-2 rounded-md border px-2 py-1.5 text-xs"
          >
            <InfoIcon className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{paidOn15Warning}</span>
          </div>
        </div>
      )}

      {/* Combined results + taxes table — income rows down the left, FICA
          columns across the top, plus Stip% / Pay-Periods stat boxes in a
          rowspan cell. The label text per row stays the i18n value (e.g.
          "Monthly Income (YTD)") so row accessible names remain stable. */}
      {result !== null && (
        <div className="bg-card mt-3 overflow-x-auto rounded-md">
          <table className="w-full text-xs">
            <caption className="sr-only">{t("review.income.calculator.title")}</caption>
            <thead>
              <tr className="bg-surface-subtle border-border border-b">
                <th
                  scope="col"
                  className="text-muted-foreground border-border border-r px-2 py-1.5 text-left font-medium"
                >
                  {t("review.income.calculator.results.columns.grossIncome")}
                </th>
                <th
                  scope="col"
                  className="text-muted-foreground border-border border-r px-2 py-1.5 text-right font-medium"
                >
                  {t("review.income.calculator.results.columns.socialSecurity")}
                </th>
                <th
                  scope="col"
                  className="text-muted-foreground border-border border-r px-2 py-1.5 text-right font-medium"
                >
                  {t("review.income.calculator.results.columns.medicare")}
                </th>
                <th scope="col" className="text-muted-foreground px-2 py-1.5 text-right font-medium">
                  {t("review.income.calculator.results.columns.combined")}
                </th>
              </tr>
            </thead>
            <tbody className="text-foreground">
              {/* Monthly Income (Period) + per-period taxes */}
              <tr className="border-border border-b">
                <td className="border-border border-r px-2 py-1.5">
                  <div className="flex justify-between gap-4">
                    <span>{t("review.income.calculator.results.monthlyIncomePeriod")}</span>
                    <span>{formatUsd(result.monthlyIncomePeriod)}</span>
                  </div>
                </td>
                <td className="border-border border-r px-2 py-1.5 text-right">
                  {formatUsd(result.taxes.socialSecurityPeriod)}
                </td>
                <td className="border-border border-r px-2 py-1.5 text-right">
                  {formatUsd(result.taxes.medicarePeriod)}
                </td>
                <td className="px-2 py-1.5 text-right">{formatUsd(result.taxes.ficaCombinedPeriod)}</td>
              </tr>

              {/* Monthly Income (YTD) — highlighted value (non-color sr-only cue) + YTD taxes */}
              <tr className="border-border border-b">
                <td className="border-border border-r px-2 py-1.5">
                  <div className="flex justify-between gap-4">
                    <span>{t("review.income.calculator.results.monthlyIncomeYtd")}</span>
                    <span className="bg-success-subtle text-success-strong rounded px-1.5 py-0.5 font-semibold">
                      <span className="sr-only">{" (highlighted): "}</span>
                      {formatUsd(result.monthlyIncomeYtd)}
                    </span>
                  </div>
                </td>
                <td className="border-border border-r px-2 py-1.5 text-right">
                  {formatUsd(result.taxes.socialSecurityYtd)}
                </td>
                <td className="border-border border-r px-2 py-1.5 text-right">{formatUsd(result.taxes.medicareYtd)}</td>
                <td className="px-2 py-1.5 text-right">{formatUsd(result.taxes.ficaCombinedYtd)}</td>
              </tr>

              {/* Weekly Income + Stip% / Pay-Periods stat boxes (span the tax columns) */}
              <tr className="border-border border-b">
                <td className="border-border border-r px-2 py-1.5">
                  <div className="flex justify-between gap-4">
                    <span>{t("review.income.calculator.results.weeklyIncome")}</span>
                    <span>{formatUsd(result.weeklyIncome)}</span>
                  </div>
                </td>
                <td rowSpan={2} colSpan={3} className="px-2 py-1.5 align-middle">
                  <div className="flex h-full items-center justify-around gap-2">
                    {/* P2 — Stip% color: green in-range (90–110), red out-of-range. */}
                    <div
                      className={cn(
                        "flex flex-col items-center rounded border px-3 py-1.5",
                        stipColorClass(result.stipPct)
                      )}
                    >
                      <span className="text-xs">{t("review.income.calculator.results.stipPct")}</span>
                      <span className="text-xs font-semibold">
                        {result.stipPct.toFixed(2)}
                        {/* Non-color a11y cue for screen readers */}
                        <span className="sr-only">
                          {result.stipPct >= 90 && result.stipPct <= 110 ? " (in range)" : " (out of range)"}
                        </span>
                      </span>
                    </div>
                    <div className="border-border bg-surface-subtle flex flex-col items-center rounded border px-3 py-1.5">
                      <span className="text-muted-foreground text-xs">
                        {t("review.income.calculator.results.payPeriods")}
                      </span>
                      <span className="text-foreground text-xs font-semibold">
                        {result.payPeriods % 1 === 0 ? result.payPeriods : result.payPeriods.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </td>
              </tr>

              {/* Bi-Weekly Income (the rowspan cell above covers the tax columns) */}
              <tr>
                <td className="border-border border-r px-2 py-1.5">
                  <div className="flex justify-between gap-4">
                    <span>{t("review.income.calculator.results.biWeeklyIncome")}</span>
                    <span>{formatUsd(result.biWeeklyIncome)}</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* P4 — Data-loss confirm dialog. Uses Radix AlertDialog (radix-ui package,
          already a dependency). Focus is trapped to the dialog; Esc cancels. */}
      <AlertDialog.Root
        open={confirmOpen}
        onOpenChange={(open) => {
          if (!open) {
            // Dialog closed without confirming: discard pending edit.
            pendingEditReference.current = null;
            setConfirmOpen(false);
          }
        }}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <AlertDialog.Content className="bg-card border-border fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border p-6 shadow-lg">
            <AlertDialog.Title className="text-foreground mb-2 text-sm font-semibold">
              {t("review.income.calculator.confirmDataLoss.title")}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-muted-foreground mb-4 text-sm">
              {t("review.income.calculator.confirmDataLoss.message")}
            </AlertDialog.Description>
            <div className="flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    // Cancel: discard the pending edit; keep current value.
                    pendingEditReference.current = null;
                    setConfirmOpen(false);
                  }}
                >
                  {t("review.income.calculator.confirmDataLoss.cancel")}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => {
                    // Confirm: clear pay-stub data, then apply the pending edit.
                    const pending = pendingEditReference.current;
                    clearPayStubData();
                    if (pending && pending.field === "grossPayPerPeriod") {
                      payPerPeriodField.onChange(pending.value);
                    }
                    pendingEditReference.current = null;
                    setConfirmOpen(false);
                  }}
                >
                  {t("review.income.calculator.confirmDataLoss.confirm")}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>

      {/* Ratio alert — opens after Calculate when ratioThreshold is "low" or "high".
          Re-fires on every qualifying Calculate click. Single "OK" action; no Cancel. */}
      <AlertDialog.Root open={ratioAlertOpen} onOpenChange={setRatioAlertOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <AlertDialog.Content className="bg-card border-border fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border p-6 shadow-lg">
            <AlertDialog.Title className="text-foreground mb-2 text-sm font-semibold">
              {t("review.income.calculator.warnings.ratioAlertTitle")}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-muted-foreground mb-4 text-sm">
              {result?.ratioThreshold === "low"
                ? t("review.income.calculator.warnings.ratioDecreased")
                : t("review.income.calculator.warnings.ratioIncreased")}
            </AlertDialog.Description>
            <div className="flex justify-end">
              <AlertDialog.Action asChild>
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => {
                    setRatioAlertOpen(false);
                  }}
                >
                  {t("review.income.calculator.warnings.ratioAlertOk")}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
